// ============================================
// DASHBOARD ADMINISTRATIVO
// src/pages/admin/AdminDashboard.tsx
// ============================================

import { useState, useEffect, useCallback } from 'react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { API_CONFIG } from '../../services/staticDataConfig';

interface MetricasSistema {
  totalEmpresas: number;
  empresasAtivas: number;
  empresasInativas: number;
  totalUsuarios: number;
  usuariosAtivos: number;
  usuariosLogadosHoje: number;
  totalConsultas: number;
  consultasHoje: number;
  consultasMes: number;
  totalPacientes: number;
  pacientesAtivos: number;
  crescimentoEmpresas: number;
  crescimentoUsuarios: number;
  crescimentoConsultas: number;
}

interface AtividadeRecente {
  id: number;
  tipo: string;
  descricao: string;
  usuario: string;
  empresa: string;
  data: string;
}

interface TopEmpresa {
  cod_empresa: number;
  nome_fantasia: string;
  total_usuarios: number;
  total_consultas_mes: number;
  ultima_atividade: string;
}

function normalizarTopEmpresa(raw: any): TopEmpresa {
  return {
    cod_empresa: raw?.cod_empresa ?? raw?.codEmpresa ?? raw?.id ?? 0,
    nome_fantasia: raw?.nome_fantasia ?? raw?.nomeFantasia ?? raw?.empresa ?? '',
    total_usuarios: raw?.total_usuarios ?? raw?.totalUsuarios ?? 0,
    total_consultas_mes: raw?.total_consultas_mes ?? raw?.totalConsultasMes ?? raw?.total_consultas ?? raw?.totalConsultas ?? 0,
    ultima_atividade: raw?.ultima_atividade ?? raw?.ultimaAtividade ?? raw?.ultimoAcesso ?? ''
  };
}

export function AdminDashboard() {
  const { admin, token } = useAdminAuth();
  const [loading, setLoading] = useState(true);
  const [metricas, setMetricas] = useState<MetricasSistema | null>(null);
  const [atividades, setAtividades] = useState<AtividadeRecente[]>([]);
  const [topEmpresas, setTopEmpresas] = useState<TopEmpresa[]>([]);
  const [erro, setErro] = useState<string | null>(null);
  const [ultimaAtualizacao, setUltimaAtualizacao] = useState<Date>(new Date());

  const carregarDados = useCallback(async () => {
    try {
      setLoading(true);
      setErro(null);

      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };

      // Carregar métricas
      const [metricasRes, atividadesRes, topEmpresasRes] = await Promise.all([
        fetch(`${API_CONFIG.BASE_URL}/admin-sistema/dashboard/metricas`, { headers }),
        fetch(`${API_CONFIG.BASE_URL}/admin-sistema/dashboard/atividades-recentes?limite=10`, { headers }),
        fetch(`${API_CONFIG.BASE_URL}/admin-sistema/dashboard/top-empresas?limite=5`, { headers })
      ]);

      if (!metricasRes.ok || !atividadesRes.ok || !topEmpresasRes.ok) {
        throw new Error('Erro ao carregar dados do dashboard');
      }

      const [metricasData, atividadesData, topEmpresasData] = await Promise.all([
        metricasRes.json(),
        atividadesRes.json(),
        topEmpresasRes.json()
      ]);

      if (metricasData.sucesso) {
        setMetricas(metricasData.dados);
      }
      if (atividadesData.sucesso) {
        setAtividades(atividadesData.dados || []);
      }
      if (topEmpresasData.sucesso) {
        const lista = Array.isArray(topEmpresasData.dados) ? topEmpresasData.dados : [];
        setTopEmpresas(lista.map(normalizarTopEmpresa));
      }

      setUltimaAtualizacao(new Date());

    } catch (error) {
      console.error('[AdminDashboard] Erro:', error);
      setErro('Erro ao carregar dados do dashboard');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    carregarDados();
    
    // Auto-refresh a cada 5 minutos
    const interval = setInterval(carregarDados, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [carregarDados]);

  function formatarData(dataString: string): string {
    try {
      const data = new Date(dataString);
      return data.toLocaleString('pt-BR');
    } catch {
      return dataString;
    }
  }

  function formatarPorcentagem(valor: number): string {
    const sinal = valor >= 0 ? '+' : '';
    return `${sinal}${valor.toFixed(1)}%`;
  }

  if (loading && !metricas) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="visually-hidden">Carregando...</span>
          </div>
          <p className="text-muted">Carregando métricas do sistema...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1">
            <i className="fas fa-tachometer-alt me-2"></i>
            Dashboard Administrativo
          </h2>
          <p className="text-muted mb-0">
            Bem-vindo, {admin?.nome}! 
            <span className="ms-2 badge bg-info">{admin?.nivel || admin?.nivelAcesso}</span>
          </p>
        </div>
        <div className="d-flex align-items-center gap-3">
          <small className="text-muted">
            Atualizado: {ultimaAtualizacao.toLocaleTimeString('pt-BR')}
          </small>
          <button 
            className="btn btn-outline-primary btn-sm"
            onClick={carregarDados}
            disabled={loading}
          >
            <i className={`fas fa-sync-alt ${loading ? 'fa-spin' : ''}`}></i>
          </button>
        </div>
      </div>

      {/* Alerta de erro */}
      {erro && (
        <div className="alert alert-danger d-flex align-items-center" role="alert">
          <i className="fas fa-exclamation-circle me-2"></i>
          <div>{erro}</div>
          <button 
            type="button" 
            className="btn-close ms-auto" 
            onClick={() => setErro(null)}
          ></button>
        </div>
      )}

      {/* Cards de métricas principais */}
      <div className="row g-4 mb-4">
        {/* Empresas */}
        <div className="col-xl-3 col-md-6">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <h6 className="text-muted text-uppercase mb-2">Empresas</h6>
                  <h2 className="mb-0">{metricas?.totalEmpresas || 0}</h2>
                  <small className="text-success">
                    <i className="fas fa-check-circle me-1"></i>
                    {metricas?.empresasAtivas || 0} ativas
                  </small>
                </div>
                <div className="bg-primary bg-opacity-10 p-3 rounded">
                  <i className="fas fa-building fa-2x text-primary"></i>
                </div>
              </div>
              {metricas?.crescimentoEmpresas !== undefined && (
                <div className="mt-3 pt-3 border-top">
                  <span className={`badge ${metricas.crescimentoEmpresas >= 0 ? 'bg-success' : 'bg-danger'}`}>
                    {formatarPorcentagem(metricas.crescimentoEmpresas)}
                  </span>
                  <small className="text-muted ms-2">vs mês anterior</small>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Usuários */}
        <div className="col-xl-3 col-md-6">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <h6 className="text-muted text-uppercase mb-2">Usuários</h6>
                  <h2 className="mb-0">{metricas?.totalUsuarios || 0}</h2>
                  <small className="text-info">
                    <i className="fas fa-user-clock me-1"></i>
                    {metricas?.usuariosLogadosHoje || 0} online hoje
                  </small>
                </div>
                <div className="bg-success bg-opacity-10 p-3 rounded">
                  <i className="fas fa-users fa-2x text-success"></i>
                </div>
              </div>
              {metricas?.crescimentoUsuarios !== undefined && (
                <div className="mt-3 pt-3 border-top">
                  <span className={`badge ${metricas.crescimentoUsuarios >= 0 ? 'bg-success' : 'bg-danger'}`}>
                    {formatarPorcentagem(metricas.crescimentoUsuarios)}
                  </span>
                  <small className="text-muted ms-2">vs mês anterior</small>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Consultas */}
        <div className="col-xl-3 col-md-6">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <h6 className="text-muted text-uppercase mb-2">Consultas</h6>
                  <h2 className="mb-0">{metricas?.consultasMes || 0}</h2>
                  <small className="text-warning">
                    <i className="fas fa-calendar-day me-1"></i>
                    {metricas?.consultasHoje || 0} hoje
                  </small>
                </div>
                <div className="bg-warning bg-opacity-10 p-3 rounded">
                  <i className="fas fa-calendar-check fa-2x text-warning"></i>
                </div>
              </div>
              {metricas?.crescimentoConsultas !== undefined && (
                <div className="mt-3 pt-3 border-top">
                  <span className={`badge ${metricas.crescimentoConsultas >= 0 ? 'bg-success' : 'bg-danger'}`}>
                    {formatarPorcentagem(metricas.crescimentoConsultas)}
                  </span>
                  <small className="text-muted ms-2">vs mês anterior</small>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Pacientes */}
        <div className="col-xl-3 col-md-6">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <h6 className="text-muted text-uppercase mb-2">Pacientes</h6>
                  <h2 className="mb-0">{metricas?.totalPacientes || 0}</h2>
                  <small className="text-success">
                    <i className="fas fa-user-check me-1"></i>
                    {metricas?.pacientesAtivos || 0} ativos
                  </small>
                </div>
                <div className="bg-info bg-opacity-10 p-3 rounded">
                  <i className="fas fa-user-injured fa-2x text-info"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Segunda linha: Top Empresas e Atividades Recentes */}
      <div className="row g-4">
        {/* Top Empresas */}
        <div className="col-xl-6">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-header bg-transparent border-0 d-flex justify-content-between align-items-center">
              <h5 className="mb-0">
                <i className="fas fa-trophy text-warning me-2"></i>
                Top Empresas
              </h5>
              <span className="badge bg-secondary">Este mês</span>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>#</th>
                      <th>Empresa</th>
                      <th className="text-center">Usuários</th>
                      <th className="text-center">Consultas</th>
                      <th>Última Atividade</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topEmpresas.length > 0 ? (
                      topEmpresas.map((empresa, index) => (
                        <tr key={empresa.cod_empresa || index}>
                          <td>
                            <span className={`badge ${index === 0 ? 'bg-warning' : index === 1 ? 'bg-secondary' : index === 2 ? 'bg-danger' : 'bg-light text-dark'}`}>
                              {index + 1}º
                            </span>
                          </td>
                          <td>
                            <strong>{empresa.nome_fantasia}</strong>
                            <br />
                            <small className="text-muted">ID: {empresa.cod_empresa}</small>
                          </td>
                          <td className="text-center">
                            <span className="badge bg-primary">
                              {empresa.total_usuarios}
                            </span>
                          </td>
                          <td className="text-center">
                            <span className="badge bg-success">
                              {empresa.total_consultas_mes}
                            </span>
                          </td>
                          <td>
                            <small className="text-muted">
                              {empresa.ultima_atividade ? formatarData(empresa.ultima_atividade) : 'N/A'}
                            </small>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={5} className="text-center text-muted py-4">
                          <i className="fas fa-inbox fa-2x mb-2 d-block"></i>
                          Nenhuma empresa encontrada
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Atividades Recentes */}
        <div className="col-xl-6">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-header bg-transparent border-0 d-flex justify-content-between align-items-center">
              <h5 className="mb-0">
                <i className="fas fa-history text-info me-2"></i>
                Atividades Recentes
              </h5>
              <a href="/admin/auditoria" className="btn btn-sm btn-outline-primary">
                Ver todas
              </a>
            </div>
            <div className="card-body p-0">
              <div className="list-group list-group-flush">
                {atividades.length > 0 ? (
                  atividades.map((atividade) => (
                    <div key={atividade.id} className="list-group-item border-0 py-3">
                      <div className="d-flex align-items-start">
                        <div className={`bg-${getTipoAtividadeCor(atividade.tipo)} bg-opacity-10 p-2 rounded me-3`}>
                          <i className={`fas ${getTipoAtividadeIcone(atividade.tipo)} text-${getTipoAtividadeCor(atividade.tipo)}`}></i>
                        </div>
                        <div className="flex-grow-1">
                          <div className="d-flex justify-content-between align-items-start">
                            <div>
                              <strong className="d-block">{atividade.descricao}</strong>
                              <small className="text-muted">
                                {atividade.usuario} • {atividade.empresa}
                              </small>
                            </div>
                            <small className="text-muted">
                              {formatarData(atividade.data)}
                            </small>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center text-muted py-4">
                    <i className="fas fa-clock fa-2x mb-2 d-block"></i>
                    Nenhuma atividade recente
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Funções auxiliares para ícones e cores de atividade
function getTipoAtividadeCor(tipo: string): string {
  const cores: Record<string, string> = {
    'login': 'success',
    'logout': 'secondary',
    'create': 'primary',
    'update': 'warning',
    'delete': 'danger',
    'consulta': 'info',
    'paciente': 'purple',
    'default': 'secondary'
  };
  return cores[tipo.toLowerCase()] || cores['default'];
}

function getTipoAtividadeIcone(tipo: string): string {
  const icones: Record<string, string> = {
    'login': 'fa-sign-in-alt',
    'logout': 'fa-sign-out-alt',
    'create': 'fa-plus',
    'update': 'fa-edit',
    'delete': 'fa-trash',
    'consulta': 'fa-calendar-check',
    'paciente': 'fa-user-injured',
    'default': 'fa-circle'
  };
  return icones[tipo.toLowerCase()] || icones['default'];
}

export default AdminDashboard;
